import React from "react";
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    Typography,
    TableBody,
    Switch,
    Stack,
    Chip,
    Box,
} from "@mui/material";
import {FeatureFlagStrategy} from "../../../types/domain/featureFlagStrategy";
import {useAtomValue} from "jotai";
import {usersLoadable} from "../../../store/usersStore";
import {Trans, useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {useCompanies} from "../../../hooks/companiesHooks";

interface FeatureFlagStrategiesTableProps {
    featureFlagStrategies: FeatureFlagStrategy[];
    setUpdateFeatureFlagStrategyDialogVisible: (visible: boolean) => void;
}

export const FeatureFlagStrategiesTable: React.FC<FeatureFlagStrategiesTableProps> = ({
    featureFlagStrategies,
    setUpdateFeatureFlagStrategyDialogVisible,
}) => {
    const {t} = useTranslation();
    const companies = useCompanies();
    const users = useAtomValue(usersLoadable);
    return featureFlagStrategies?.length ? (
        <TableContainer sx={{marginTop: "1rem", maxHeight: "70vh", overflowY: "auto"}}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Typography fontWeight="bold">{t("feature_flags_page.active")}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight="bold">{t("feature_flags_page.selector")}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight="bold">{t("feature_flags_page.companies")}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight="bold">{t("feature_flags_page.createdby")}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight="bold">{t("feature_flags_page.createdat")}</Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {featureFlagStrategies.map((strategy, index) => (
                        <TableRow
                            key={strategy.id}
                            sx={{opacity: index === 0 ? 1 : 0.5, backgroundColor: index === 0 ? "#edeef6" : ""}}
                        >
                            <TableCell>
                                <Switch checked={strategy.config.enabled} disabled={true} />
                            </TableCell>
                            <TableCell sx={{whiteSpace: "nowrap"}}>
                                {t("feature_flags_page." + strategy.selector)}
                            </TableCell>
                            <TableCell sx={{width: "100%"}}>
                                {companies.isSuccess &&
                                    (["ALL", "NONE"].includes(strategy.selector) ? (
                                        t("feature_flags_page." + strategy.selector)
                                    ) : (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            sx={{overflowY: "auto", overflowX: "hidden", maxHeight: "160px"}}
                                        >
                                            {strategy.companyIds.map((companyId) => {
                                                const company = companies?.data?.find((c) => c.id === companyId);
                                                return company ? (
                                                    <Chip
                                                        disabled={index !== 0}
                                                        key={companyId}
                                                        label={company?.properties?.name}
                                                        sx={{marginBottom: "4px !important"}}
                                                    />
                                                ) : null;
                                            })}
                                        </Stack>
                                    ))}
                            </TableCell>
                            <TableCell sx={{whiteSpace: "nowrap"}}>
                                {users?.state === "hasData"
                                    ? (() => {
                                          const user = users.data.find((u) => u.id === strategy.createdBy);
                                          return user
                                              ? `${user?.properties?.firstName} ${user?.properties?.lastName}`
                                              : strategy.createdBy;
                                      })()
                                    : strategy.createdBy}
                            </TableCell>
                            <TableCell sx={{whiteSpace: "nowrap"}}>
                                {dayjs(strategy.createdAt).format("MMMM D, YYYY h:mm A")}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    ) : (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            sx={{marginTop: "2rem"}}
        >
            <Typography variant="h3" color="textSecondary">
                <Trans
                    i18nKey="feature_flags_page.no_strategy_message"
                    components={{
                        button: (
                            <Typography
                                component="span"
                                onClick={() => {
                                    setUpdateFeatureFlagStrategyDialogVisible(true);
                                }}
                                sx={{
                                    color: "primary.main",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                }}
                            ></Typography>
                        ),
                    }}
                />
            </Typography>
        </Box>
    );
};
